import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  NAME_MAX_LEN,
  claimDisplayName,
  suggestGuestName,
  validateName,
} from '@/lib/guestIdentity';
import { track, EVT } from '@/lib/telemetry';

const LOGO = '/kickard-shield.png';

/**
 * Broadcast-styled "Choose your name" modal.
 *
 * Two modes:
 *   • first-time   → title "CHOOSE YOUR NAME", no Cancel button
 *   • edit         → title "EDIT NAME", Cancel button visible
 *
 * Props:
 *   mode      'first' | 'edit'
 *   initial   prefill string (for 'edit')
 *   onSaved   (guest) => void
 *   onCancel  () => void  (only used in 'edit')
 */
export default function GuestNameModal({
  mode = 'first',
  initial = '',
  onSaved,
  onCancel,
}) {
  const [name, setName] = useState(initial || (mode === 'first' ? '' : ''));
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus the input on mount
    const t = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 60);
    return () => clearTimeout(t);
  }, []);

  async function handleSave() {
    const v = validateName(name);
    if (!v.ok) {
      setError(v.error || 'Invalid name');
      setSuggestions([]);
      return;
    }
    setSaving(true);
    setSuggestions([]);
    const res = await claimDisplayName(v.cleaned);
    setSaving(false);
    if (!res.ok) {
      // Phase 3.8.1 — friendlier conflict copy for the "I just installed
      // the PWA and lost my profile" case. If the user is on first-launch
      // (no existing displayName) and the name they typed is already
      // taken, hint at the recovery path. We do NOT auto-restore here
      // because the cookie path runs at boot in App.js — if it failed,
      // the cookie was either missing or the server didn't recognise
      // the guestId, in which case the user genuinely needs a fresh name.
      if (res.taken) {
        if (mode === 'first') {
          setError('This name is already claimed. If it is yours, open Kickard from the same browser you originally signed up in (cookies sync your profile to the PWA).');
        } else {
          setError(res.error || 'Name already taken. Pick another.');
        }
      } else {
        setError(res.error || 'Could not save');
      }
      if (res.taken && Array.isArray(res.suggestions)) {
        setSuggestions(res.suggestions);
      }
      return;
    }
    track(mode === 'edit' ? EVT.GUEST_NAME_EDITED : EVT.GUEST_NAME_CREATED, {
      metadata: { nameLength: v.cleaned.length },
    });
    if (onSaved) onSaved(res.guest);
  }

  function handleSuggest() {
    const suggestion = suggestGuestName();
    setName(suggestion);
    setError(null);
    setSuggestions([]);
  }

  function applySuggestion(s) {
    setName(s);
    setError(null);
    setSuggestions([]);
  }

  function handleKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape' && mode === 'edit' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  }

  const liveValidation = validateName(name);
  const canSave = liveValidation.ok;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="guest-name-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[100] flex items-center justify-center px-4"
        style={{
          background:
            'radial-gradient(ellipse at 50% 24%, hsl(46 95% 60% / 0.10) 0%, hsl(0 0% 0% / 0.92) 70%)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 12, opacity: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-[360px] relative"
          style={{
            background:
              'linear-gradient(180deg, hsl(0 0% 6% / 0.96) 0%, hsl(0 0% 3% / 0.96) 100%)',
            border: '1px solid hsl(46 65% 35% / 0.55)',
            borderRadius: 10,
            boxShadow:
              '0 0 0 1px hsl(46 95% 60% / 0.08), 0 24px 60px -12px rgba(0,0,0,0.85)',
          }}
        >
          {/* Top gold rule */}
          <div
            className="absolute top-0 left-0 right-0 h-[2px] pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, hsl(46 95% 60%) 50%, transparent 100%)',
            }}
          />

          <div className="px-6 pt-6 pb-5 flex flex-col items-center">
            <img
              src={LOGO}
              alt="Kickard"
              style={{ width: 56, background: 'transparent' }}
              data-testid="guest-modal-logo"
            />
            <div
              className="font-heading uppercase mt-3 text-[hsl(var(--k-gold-bright))]"
              style={{ fontSize: 22, letterSpacing: '0.22em' }}
            >
              {mode === 'edit' ? 'EDIT NAME' : 'CHOOSE YOUR NAME'}
            </div>

            {/* hairline rule */}
            <div className="flex items-center gap-3 mt-2">
              <span className="block w-8 h-px bg-[hsl(var(--sb-rule)/0.55)]" />
              <span className="font-body text-[9px] text-white/55 uppercase tracking-[0.32em]">
                {mode === 'edit' ? 'Update display name' : 'Used in online matches'}
              </span>
              <span className="block w-8 h-px bg-[hsl(var(--sb-rule)/0.55)]" />
            </div>
          </div>

          <div className="px-6 pb-6 flex flex-col gap-3">
            <input
              ref={inputRef}
              data-testid="guest-name-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKey}
              placeholder="Your name"
              maxLength={NAME_MAX_LEN}
              autoComplete="off"
              spellCheck={false}
              className="w-full px-4 py-3 rounded bg-white/5 border border-white/10 text-white placeholder-white/40 outline-none focus:border-[hsl(var(--k-gold))] text-center font-heading text-lg tracking-[0.18em]"
            />

            <div className="flex items-center justify-between text-[10px] font-mono tracking-[0.18em] uppercase text-white/45">
              <span data-testid="guest-name-counter">
                {liveValidation.cleaned.length}/{NAME_MAX_LEN}
              </span>
              <button
                type="button"
                onClick={handleSuggest}
                data-testid="guest-name-suggest-btn"
                className="text-[hsl(var(--k-gold-bright))] hover:underline"
              >
                Suggest
              </button>
            </div>

            {error && (
              <div
                data-testid="guest-name-error"
                className="text-red-300 text-center text-[12px] font-body"
              >
                {error}
              </div>
            )}

            {suggestions.length > 0 && (
              <div data-testid="guest-name-suggestions" className="flex flex-wrap gap-2 justify-center">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`guest-name-suggestion-${s}`}
                    onClick={() => applySuggestion(s)}
                    className="px-3 py-1.5 rounded-full border border-[hsl(var(--k-gold)/0.45)] bg-black/40 text-[hsl(var(--k-gold-bright))] text-[11px] font-heading tracking-[0.2em] hover:bg-black/60 active:scale-95 transition-transform"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <button
              data-testid="guest-name-save-btn"
              className="btn-primary mt-1"
              onClick={handleSave}
              disabled={!canSave || saving}
              style={(!canSave || saving) ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              {saving ? 'SAVING…' : (mode === 'edit' ? 'SAVE' : 'CONTINUE')}
            </button>

            {mode === 'edit' && onCancel && (
              <button
                data-testid="guest-name-cancel-btn"
                className="btn-secondary"
                onClick={onCancel}
              >
                CANCEL
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
